import React, { useState, useEffect } from "react";
import { dataLoaderUri } from "../API/API";
import apiClient from "../services/apiClient";
import BackButton from "../shared/BackButton/BackButton";
import styles from "./LanguageInstructions.module.css";

interface LanguageInstruction {
  languageCode: string;
  languageName: string;
  vocabularyInstructions: string;
  exerciseInstructions: string;
  grammarNotes: string;
  isActive: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

const LanguageInstructions: React.FC = () => {
  const [languages, setLanguages] = useState<LanguageInstruction[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [currentInstruction, setCurrentInstruction] = useState<LanguageInstruction | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");

  // Fetch all language instructions on mount
  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get(`${dataLoaderUri}/api/v1/language-instructions`);
      if (data?.languages) {
        setLanguages(data.languages);
      }
    } catch (error) {
      console.error("Error fetching language instructions:", error);
      setMessage("Error loading language instructions");
    } finally {
      setLoading(false);
    }
  };

  const selectLanguage = (langCode: string) => {
    const lang = languages.find(l => l.languageCode === langCode);
    if (lang) {
      setSelectedLanguage(langCode);
      setCurrentInstruction({ ...lang });
    }
  };

  const updateField = (field: keyof LanguageInstruction, value: string | boolean) => {
    if (currentInstruction) {
      setCurrentInstruction({
        ...currentInstruction,
        [field]: value
      });
    }
  };

  const saveInstructions = async () => {
    if (!currentInstruction) return;

    setSaving(true);
    try {
      const payload = {
        languageCode: currentInstruction.languageCode,
        languageName: currentInstruction.languageName,
        vocabularyInstructions: currentInstruction.vocabularyInstructions,
        exerciseInstructions: currentInstruction.exerciseInstructions,
        grammarNotes: currentInstruction.grammarNotes,
        isActive: currentInstruction.isActive
      };

      await apiClient.post(`${dataLoaderUri}/api/v1/language-instructions`, payload);

      setMessage(`Saved instructions for ${currentInstruction.languageName}`);

      // Update local state
      setLanguages(prev => prev.map(lang =>
        lang.languageCode === currentInstruction.languageCode
          ? { ...currentInstruction, updatedAt: new Date().toISOString(), updatedBy: "web-admin" }
          : lang
      ));
    } catch (error) {
      console.error("Error saving instructions:", error);
      setMessage("Error saving instructions");
    } finally {
      setSaving(false);
    }
  };

  const hasInstructions = (lang: LanguageInstruction): boolean => {
    return !!(lang.vocabularyInstructions || lang.exerciseInstructions || lang.grammarNotes);
  };

  return (
    <div className={styles.container}>
      <BackButton />
      <h1 className={styles.title}>Language Generation Instructions</h1>
      <p className={styles.subtitle}>
        Configure custom instructions for AI-powered vocabulary generation per language.
        These instructions help ensure language-specific details are included (e.g., articles for gendered nouns).
      </p>

      {loading && <div className={styles.loading}>Loading languages...</div>}

      {message && (
        <div className={`${styles.message} ${message.includes('Error') ? styles.error : styles.success}`}>
          {message}
          <button onClick={() => setMessage("")} className={styles.dismissButton}>x</button>
        </div>
      )}

      <div className={styles.layout}>
        {/* Language List */}
        <div className={styles.languageList}>
          <h2>Languages</h2>
          {languages.map(lang => (
            <div
              key={lang.languageCode}
              className={`${styles.languageItem} ${selectedLanguage === lang.languageCode ? styles.selected : ''}`}
              onClick={() => selectLanguage(lang.languageCode)}
            >
              <div className={styles.languageInfo}>
                <span className={styles.languageName}>{lang.languageName}</span>
                <span className={styles.languageCode}>{lang.languageCode}</span>
              </div>
              <div className={styles.languageStatus}>
                {hasInstructions(lang) && (
                  <span className={styles.configuredBadge}>Configured</span>
                )}
                {!lang.isActive && (
                  <span className={styles.inactiveBadge}>Inactive</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Instruction Editor */}
        <div className={styles.editor}>
          {currentInstruction ? (
            <>
              <div className={styles.editorHeader}>
                <h2>{currentInstruction.languageName} Instructions</h2>
                <label className={styles.activeToggle}>
                  <input
                    type="checkbox"
                    checked={currentInstruction.isActive}
                    onChange={(e) => updateField('isActive', e.target.checked)}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                  Vocabulary Instructions
                  <span className={styles.fieldHint}>
                    Instructions for generating vocabulary items (e.g., "Include definite articles with all nouns: el/la for Spanish")
                  </span>
                </label>
                <textarea
                  value={currentInstruction.vocabularyInstructions}
                  onChange={(e) => updateField('vocabularyInstructions', e.target.value)}
                  className={styles.textarea}
                  rows={6}
                  placeholder={`Example for Spanish:
- Include definite articles (el/la) with all nouns
- Include gender information for nouns
- For verbs, include the infinitive form`}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                  Exercise Instructions
                  <span className={styles.fieldHint}>
                    Instructions for generating exercises (fill-in-blank, conversation objectives, etc.)
                  </span>
                </label>
                <textarea
                  value={currentInstruction.exerciseInstructions}
                  onChange={(e) => updateField('exerciseInstructions', e.target.value)}
                  className={styles.textarea}
                  rows={6}
                  placeholder={`Example for Spanish:
- Use formal "usted" conjugations for beginner content
- Include context about verb conjugation patterns
- For fill-in-blank, test article agreement`}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                  Grammar Notes
                  <span className={styles.fieldHint}>
                    Additional grammar notes to consider during generation
                  </span>
                </label>
                <textarea
                  value={currentInstruction.grammarNotes}
                  onChange={(e) => updateField('grammarNotes', e.target.value)}
                  className={styles.textarea}
                  rows={4}
                  placeholder={`Example for German:
- German has three genders: der (masculine), die (feminine), das (neuter)
- Adjective endings change based on case and article type
- Word order follows V2 rule in main clauses`}
                />
              </div>

              {currentInstruction.updatedAt && (
                <div className={styles.metadata}>
                  Last updated: {new Date(currentInstruction.updatedAt).toLocaleString()}
                  {currentInstruction.updatedBy && ` by ${currentInstruction.updatedBy}`}
                </div>
              )}

              <button
                onClick={saveInstructions}
                disabled={saving}
                className={styles.saveButton}
              >
                {saving ? "Saving..." : "Save Instructions"}
              </button>
            </>
          ) : (
            <div className={styles.placeholder}>
              <p>Select a language from the list to configure its generation instructions.</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Examples */}
      <div className={styles.examples}>
        <h3>Common Use Cases</h3>
        <div className={styles.exampleGrid}>
          <div className={styles.exampleCard}>
            <h4>Spanish/French/Italian/Portuguese</h4>
            <p>Include definite articles (el/la, le/la, il/la, o/a) with nouns to indicate gender.</p>
          </div>
          <div className={styles.exampleCard}>
            <h4>German</h4>
            <p>Include articles (der/die/das) and plural forms. Consider noun capitalization.</p>
          </div>
          <div className={styles.exampleCard}>
            <h4>Japanese/Korean</h4>
            <p>Include pronunciation guides (hiragana/romaji or hangul/romanization).</p>
          </div>
          <div className={styles.exampleCard}>
            <h4>Chinese</h4>
            <p>Include pinyin with tone marks for proper pronunciation guidance.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageInstructions;
